import { useCallback, useLayoutEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { arrowHead, chainLayout, onPaper } from './layout.ts';
import { stepName, type TileLine, type TilePhase, type TileStep } from './model.ts';
import { Tile } from './Tile.tsx';

/**
 * A step opened (design study 10, open, Jason: "the expanded node, with chain alone up and down"; study 12, open A):
 * the step itself in the middle as its card, what it waits on above it a row for each step back, all the way back,
 * and everything it opens below, all the way on (Jason, 2026-10-07: "i never asked you to hide all of this, you should
 * show the full chain"), every other step out of the way. Its lines are dark. Any step of the chain is a click from
 * being the one opened; a click on the paper round it closes it (`onClose`), as Esc does. Under it, on a job, what the
 * tree offers after it (`after`), dashed, with what each carries at its foot. Where it waits on a step of an earlier
 * phase, or a step of a later phase waits on it, the chain runs through the gates between the phases (`phases`; Jason,
 * 2026-10-07: "focusing on a step node shows the whole chain going through the gates"), each leg dark like the rest but
 * not a wait to take out.
 */


/** No phases: nothing to run through. */
const NO_PHASES: readonly Pick<TilePhase, 'key' | 'closedBy'>[] = [];

export function FocusChain({
  steps,
  lines,
  phases = NO_PHASES,
  focus,
  card,
  label,
  after,
  onFocus,
  onClose,
  onLine,
  cardWidth,
}: {
  steps: readonly TileStep[];
  lines: readonly TileLine[];
  /** The phases, in order, each with the gate that closes it: what the chain runs through between them. */
  phases?: readonly Pick<TilePhase, 'key' | 'closedBy'>[];
  focus: string;
  card: ReactNode;
  label: string;
  /** Below the chain: steps the tree offers after it, and what each carries at its foot. */
  after?: { title: string; steps: TileStep[]; actionsFor?: (key: string) => ReactNode };
  onFocus: (key: string) => void;
  /** A click on the paper round the chain: back to the whole tree. */
  onClose?: () => void;
  /** A line of the chain clicked: the page asks what to do with it (take it out). */
  onLine?: (line: TileLine) => void;
  /** How wide its card is drawn, when wider than every tree's (the master's, L-38). */
  cardWidth?: number;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  // Where the press began: a click closes only when it began and ended on the paper (the card may redraw under it).
  const pressedPaper = useRef(false);
  const [width, setWidth] = useState<number | null>(null);
  const [cardH, setCardH] = useState(420);
  // The card's height, watched from when it is first drawn: what comes below it makes room for it as it grows.
  const watching = useRef<ResizeObserver | null>(null);
  const cardRef = useCallback((el: HTMLDivElement | null) => {
    watching.current?.disconnect();
    watching.current = null;
    if (!el) return;
    setCardH(el.offsetHeight);
    const watch = new ResizeObserver(() => setCardH(el.offsetHeight));
    watch.observe(el);
    watching.current = watch;
  }, []);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  const byKey = useMemo(() => new Map([...steps, ...(after?.steps ?? [])].map((s) => [s.key, s])), [steps, after]);
  const layout = useMemo(() => (width ? chainLayout(steps, lines, focus, width, cardH, after ?? null, phases, cardWidth) : null), [steps, lines, focus, width, cardH, after, phases, cardWidth]);
  if (!layout) return <div ref={box} className="min-h-40" role="group" aria-label={label} />;
  const { height, boxes, card: cardBox, labels, offered } = layout;
  const step = byKey.get(focus);

  const press = (e: PointerEvent<HTMLDivElement>) => {
    pressedPaper.current = onPaper(e.target);
  };
  const click = (e: MouseEvent<HTMLDivElement>) => {
    if (onClose && pressedPaper.current && onPaper(e.target) && !window.getSelection()?.toString()) onClose();
    pressedPaper.current = false;
  };

  return (
    <div ref={box} role="group" aria-label={label}>
      <div className="relative" style={{ height }} onPointerDown={press} onClick={click}>
        {labels.map((l) => (
          <p key={`${l.y}-${l.text}`} className="label absolute left-0" style={{ top: l.y }}>
            {l.text}
          </p>
        ))}
        <svg className="absolute left-0 top-0 z-[1] overflow-visible" width="100%" height={height} aria-hidden={!onLine}>
          {layout.lines.map((w) => {
            const ghost = w.offered;
            return (
              <g key={w.id}>
                <path d={w.d} fill="none" className={ghost ? 'stroke-ink-3' : 'stroke-ink'} strokeWidth={ghost ? 1.2 : 1.8} strokeDasharray={ghost ? '4 3' : undefined} />
                <path d={arrowHead(w.head)} className={ghost ? 'fill-ink-3' : 'fill-ink'} />
                {onLine && w.line ? (
                  <path data-line="" d={w.d} fill="none" stroke="transparent" strokeWidth="14" className="cursor-pointer" onClick={() => onLine(w.line!)}>
                    <title>{`${stepName(byKey.get(w.dst)?.title ?? '')} waits on ${stepName(byKey.get(w.src)?.title ?? '')}: click to take it out`}</title>
                  </path>
                ) : null}
              </g>
            );
          })}
        </svg>
        {[...boxes].map(([k, b]) => {
          const s = byKey.get(k);
          if (!s) return null;
          return <Tile key={k} step={s} x={b.x} y={b.y} h={b.h} actions={offered.has(k) ? after?.actionsFor?.(k) : undefined} onOpen={offered.has(k) ? undefined : () => onFocus(k)} />;
        })}
        <div
          ref={cardRef}
          data-card=""
          className="absolute z-[4] scroll-mt-24 rounded-[2px] border-[1.5px] border-ink bg-sheet shadow-[0_10px_30px_-18px_rgba(32,35,31,0.55)]"
          style={{ left: cardBox.x, top: cardBox.y, width: cardBox.w }}
          aria-label={step ? stepName(step.title) : undefined}
        >
          {card}
        </div>
      </div>
    </div>
  );
}
